/*
 * Converts formatted clipboard content (Word, Google Docs, web pages) into the
 * Markdown the prompt editor uses, so pasted headings, bold, lists, etc. keep
 * their formatting. Text is taken from the DOM (never re-parsed as HTML), so a
 * literal "<tag>" in the source document arrives as text.
 */

type Format = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  code: boolean;
};

type Run = Format & { text: string; size?: number };

type Line = {
  prefix: string;
  text: string;
  size?: number;
  length: number;
  // A Markdown table row: never turned into a heading
  table?: boolean;
};

type Context = {
  format: Format;
  inHeading: boolean;
  listDepth: number;
  ordered: boolean;
  fontSize?: number;
  // Inside content the editor has no format for: keep the text only
  plain?: boolean;
};

type StyleProps = Partial<Record<(typeof STYLE_PROPS)[number], string>>;

const STYLE_PROPS = [
  'font-weight',
  'font-style',
  'text-decoration',
  'text-decoration-line',
  'font-size',
  'display',
  'visibility',
  'opacity',
] as const;

const PLAIN: Format = {
  bold: false,
  italic: false,
  underline: false,
  code: false,
};

const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'LI',
  'BLOCKQUOTE',
  'PRE',
  'TR',
  'SECTION',
  'ARTICLE',
  'HEADER',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'UL',
  'OL',
  'TABLE',
  'TBODY',
  'THEAD',
]);

const SKIP_TAGS = new Set([
  'STYLE',
  'SCRIPT',
  'HEAD',
  'TITLE',
  'META',
  'LINK',
  'XML',
  // Never shown on the source page
  'NOSCRIPT',
  'TEMPLATE',
]);

/**
 * Content the source page doesn't show. It is dropped so a copied web page
 * can't slip hidden instructions into the prompt (a prompt-injection risk).
 */
const isHidden = (
  element: HTMLElement,
  style: StyleProps,
  styleAttr: string
): boolean =>
  element.hasAttribute('hidden') ||
  style.display === 'none' ||
  style.visibility === 'hidden' ||
  style.visibility === 'collapse' ||
  Number(style.opacity) === 0 ||
  toPx(style['font-size']) === 0 ||
  // Word's hidden text
  /mso-hide:\s*all/i.test(styleAttr);

// Characters that don't show on screen but are read by the model, a known way
// to smuggle instructions into LLM prompts: Unicode tag characters and
// variation selectors used to encode hidden text, zero-width spaces, word
// joiner, byte order mark, soft hyphen, and bidirectional override/isolate
// controls (which can also make text display in a different order).
// Zero-width joiner/non-joiner and LRM/RLM are kept: emoji and some scripts
// need them.
const INVISIBLE_RANGES: Array<[number, number]> = [
  [0xe0000, 0xe007f], // tag characters
  [0xe0100, 0xe01ef], // variation selectors supplement
  [0x200b, 0x200b], // zero-width space
  [0x2060, 0x2060], // word joiner
  [0xfeff, 0xfeff], // byte order mark / zero-width no-break space
  [0x00ad, 0x00ad], // soft hyphen
  [0x202a, 0x202e], // bidirectional embeddings and overrides
  [0x2066, 0x2069], // bidirectional isolates
];

const isInvisible = (character: string) => {
  const code = character.codePointAt(0) ?? 0;
  return INVISIBLE_RANGES.some(([from, to]) => code >= from && code <= to);
};

/** Remove invisible characters from pasted text (see INVISIBLE_RANGES). */
export const removeInvisibleCharacters = (text: string): string =>
  Array.from(text)
    .filter((character) => !isInvisible(character))
    .join('');

const hasFormatting = (format: Format) =>
  format.bold || format.italic || format.underline || format.code;

const sameFormat = (a: Format, b: Format) =>
  a.bold === b.bold &&
  a.italic === b.italic &&
  a.underline === b.underline &&
  a.code === b.code;

/**
 * Resolves the formatting-related styles of an element from the pasted
 * document's <style> rules plus its inline style. Apple apps (Pages, TextEdit,
 * Notes) put underline and font sizes in class rules such as
 * `span.s2 {text-decoration: underline}`. The CSS is only parsed, never
 * applied, so nothing it references is loaded.
 */
const createStyleResolver = (doc: Document) => {
  const rules: CSSStyleRule[] = [];
  doc.querySelectorAll('style').forEach((styleElement) => {
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(styleElement.textContent ?? '');
      Array.from(sheet.cssRules).forEach((rule) => {
        if (rule instanceof CSSStyleRule) rules.push(rule);
      });
    } catch {
      // Unparsable or unsupported stylesheet: fall back to inline styles
    }
  });

  return (element: HTMLElement): StyleProps => {
    const resolved: StyleProps = {};
    const apply = (style: CSSStyleDeclaration) =>
      STYLE_PROPS.forEach((prop) => {
        const value = style.getPropertyValue(prop);
        if (value) resolved[prop] = value;
      });

    rules.forEach((rule) => {
      try {
        if (element.matches(rule.selectorText)) apply(rule.style);
      } catch {
        // Selector the browser can't match (e.g. Word's mso-specific ones)
      }
    });
    apply(element.style);
    return resolved;
  };
};

const toPx = (value?: string) => {
  const match = value?.match(/^([\d.]+)(px|pt)$/);
  if (!match) return undefined;
  return match[2] === 'pt' ? (Number(match[1]) * 4) / 3 : Number(match[1]);
};

const inlineFormat = (
  element: HTMLElement,
  parent: Format,
  style: StyleProps
): Format => {
  const format = { ...parent };
  const tag = element.tagName;

  if (tag === 'B' || tag === 'STRONG') format.bold = true;
  if (tag === 'I' || tag === 'EM') format.italic = true;
  if (tag === 'U' || tag === 'INS') format.underline = true;
  if (tag === 'CODE' || tag === 'KBD' || tag === 'SAMP') format.code = true;

  // Inline styles override tags (Google Docs wraps everything in
  // <b style="font-weight:normal">).
  const weight = style['font-weight'] ?? '';
  if (weight === 'bold' || Number(weight) >= 600) format.bold = true;
  if (weight === 'normal' || (Number(weight) > 0 && Number(weight) < 600)) {
    format.bold = false;
  }
  if (style['font-style'] === 'italic') format.italic = true;
  if (style['font-style'] === 'normal') format.italic = false;

  const decoration = `${style['text-decoration'] ?? ''} ${
    style['text-decoration-line'] ?? ''
  }`;
  if (decoration.includes('underline')) format.underline = true;
  if (/^\s*none\b/.test(style['text-decoration'] ?? '')) {
    format.underline = false;
  }

  return format;
};

const wrapRun = (text: string, format: Format) => {
  const lead = text.match(/^ */)?.[0] ?? '';
  const trail = text.slice(lead.length).match(/ *$/)?.[0] ?? '';
  let core = text.slice(lead.length, text.length - trail.length);
  if (!core) return text;

  if (format.code) {
    core = `\`${core}\``;
  } else {
    if (format.underline) core = `<u>${core}</u>`;
    // "_" inside "**" so bold italic renders as **_text_**, not ***text***
    if (format.italic) core = format.bold ? `_${core}_` : `*${core}*`;
    if (format.bold) core = `**${core}**`;
  }
  return lead + core + trail;
};

const renderRuns = (runs: Run[]) => {
  const merged: Run[] = [];
  for (const run of runs) {
    const last = merged[merged.length - 1];
    if (last && sameFormat(last, run)) last.text += run.text;
    else merged.push({ ...run });
  }
  return merged
    .map((run) => wrapRun(run.text, run))
    .join('')
    .trim()
    .replace(/\u00a0/g, ' ');
};

type StyleResolver = ReturnType<typeof createStyleResolver>;

type Conversion = {
  lines: Line[];
  formatted: boolean;
  hasHeadingMarkup: boolean;
};

/** Returns Markdown, or null when the HTML carries no formatting worth keeping. */
export function htmlToMarkdown(html: string): string | null {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const { lines, formatted, hasHeadingMarkup } = convertNode(
    doc.body,
    createStyleResolver(doc),
    { format: PLAIN, inHeading: false, listDepth: 0, ordered: false }
  );

  const hasHeadings = !hasHeadingMarkup && markHeadingsBySize(lines);
  if (!formatted && !hasHeadings) return null;

  const output = lines.map((line) => line.prefix + line.text);
  // Drop blank lines at the edges; keep blank lines between paragraphs.
  while (output.length && !output[0].trim()) output.shift();
  while (output.length && !output[output.length - 1].trim()) output.pop();
  return output.join('\n');
}

/**
 * An HTML table as Markdown table rows: the first row is the header, merged
 * cells are split into empty cells, short rows are padded, and "|" in cell
 * text is escaped. Each cell is converted on its own and its lines joined with
 * spaces, since a Markdown table cell holds a single line.
 */
const tableToMarkdownRows = (
  table: HTMLElement,
  resolveStyle: StyleResolver,
  convertCell: (cell: HTMLElement) => string
): string[] => {
  const rows = Array.from(table.querySelectorAll('tr'))
    .filter((row) => row.closest('table') === table)
    .filter(
      (row) =>
        !isHidden(row, resolveStyle(row), row.getAttribute('style') ?? '') &&
        // Word adds an invisible zero-height row to fix column widths
        row.getAttribute('height') !== '0'
    )
    .map((row) =>
      Array.from(row.children)
        .filter((cell) => cell.tagName === 'TD' || cell.tagName === 'TH')
        .flatMap((cell) => {
          const text = convertCell(cell as HTMLElement).replace(/\|/g, '\\|');
          const span = Math.min(
            Math.max(Number(cell.getAttribute('colspan')) || 1, 1),
            MAX_COLSPAN
          );
          return [text, ...Array<string>(span - 1).fill('')];
        })
    )
    .filter((cells) => cells.length);

  if (!rows.length) return [];
  const columns = Math.max(...rows.map((cells) => cells.length));
  const format = (cells: string[]) =>
    `| ${Array.from({ length: columns }, (_, index) => cells[index] ?? '').join(' | ')} |`;

  // Header cells are shown bold anyway; drop bold that wraps a whole cell
  const header = rows[0].map((cell) => {
    const bold = cell.match(/^\*\*(.+)\*\*$/);
    return bold && !bold[1].includes('**') ? bold[1] : cell;
  });

  return [
    format(header),
    `| ${Array<string>(columns).fill('---').join(' | ')} |`,
    ...rows.slice(1).map(format),
  ];
};

const MAX_COLSPAN = 50;

function convertNode(
  root: Node,
  resolveStyle: StyleResolver,
  rootContext: Context
): Conversion {
  const lines: Line[] = [];
  let hasHeadingMarkup = false;
  let runs: Run[] | null = null;
  let prefix = '';
  let formatted = false;

  // Set after a <br>; a <br> that ends a block adds no extra line.
  let afterBreak = false;

  const lineText = () => (runs ?? []).map((run) => run.text).join('');
  const isBlank = () =>
    !lineText()
      .replace(/\u00a0/g, ' ')
      .trim();

  /** Ends the current line. Blank lines are kept only when `keepBlank`. */
  const flush = (keepBlank: boolean) => {
    if (runs && (keepBlank || !isBlank())) {
      // The line's font size is the size of most of its text
      const sizes = new Map<number, number>();
      runs.forEach((run) => {
        const length = run.text.trim().length;
        if (run.size && length) {
          sizes.set(run.size, (sizes.get(run.size) ?? 0) + length);
        }
      });
      const size = [...sizes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
      lines.push({
        prefix,
        text: renderRuns(runs),
        size,
        length: lineText().trim().length,
      });
    }
    runs = null;
    prefix = '';
    afterBreak = false;
  };

  const setPrefix = (value: string) => {
    if (!value) return;
    formatted = true;
    // A <p> inside an <li> keeps the list marker set by the <li>
    if (!prefix) prefix = value;
  };

  const pushText = (raw: string, format: Format, size?: number) => {
    let text = raw.replace(/[ \t\r\n]+/g, ' ');
    if (!runs) runs = [];
    if (/(^| )$/.test(lineText()) && text.startsWith(' ')) {
      text = text.slice(1);
    }
    if (!text) return;
    if (text.trim()) afterBreak = false;
    if (hasFormatting(format) && text.trim()) formatted = true;
    runs.push({ ...format, text, size });
  };

  const walk = (node: Node, context: Context) => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushText(
        node.textContent ?? '',
        context.plain ? PLAIN : context.format,
        context.fontSize
      );
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node as HTMLElement;
    const tag = element.tagName.toUpperCase();
    const styleAttr = element.getAttribute('style') ?? '';
    const className = element.getAttribute('class') ?? '';

    if (SKIP_TAGS.has(tag)) return;
    // Word's list bullet/number, emitted as text; handled via mso-list below
    if (/mso-list:\s*Ignore/i.test(styleAttr)) return;

    const style = resolveStyle(element);
    if (isHidden(element, style, styleAttr)) return;

    if (tag === 'BR') {
      const pending = prefix;
      flush(true);
      runs = [];
      prefix = /^\s*>/.test(pending) ? pending : '';
      afterBreak = true;
      return;
    }
    if (tag === 'HR') {
      // Dividers aren't a prompt format: keep only the line break
      flush(false);
      lines.push({ prefix: '', text: '', length: 0 });
      return;
    }
    if (tag === 'TABLE') {
      flush(false);
      const convertCell = (cell: HTMLElement) => {
        const cellContent = convertNode(cell, resolveStyle, {
          ...rootContext,
          format: context.format,
          plain: context.plain,
        });
        if (cellContent.formatted) formatted = true;
        return cellContent.lines
          .map((line) => line.text)
          .filter((text) => text.trim())
          .join(' ');
      };
      // A caption becomes a line of text above the table
      const caption = Array.from(element.children).find(
        (child) => child.tagName === 'CAPTION'
      );
      const captionText = caption ? convertCell(caption as HTMLElement) : '';
      if (captionText) {
        lines.push({
          prefix: '',
          text: captionText,
          length: captionText.length,
        });
      }
      const rows = tableToMarkdownRows(element, resolveStyle, convertCell);
      rows.forEach((text) =>
        lines.push({ prefix: '', text, length: text.length, table: true })
      );
      if (rows.length) formatted = true;
      return;
    }

    let format = inlineFormat(element, context.format, style);
    const childContext: Context = {
      ...context,
      format,
      fontSize: toPx(style['font-size']) ?? context.fontSize,
    };

    if (!BLOCK_TAGS.has(tag)) {
      if (context.inHeading) childContext.format = { ...format, bold: false };
      if (tag === 'TD' || tag === 'TH') {
        // A cell outside a <table> (e.g. part of a copied row)
        if (lineText().trim()) pushText(' | ', PLAIN);
      }
      element.childNodes.forEach((child) => walk(child, childContext));
      return;
    }

    // Entering a block: drop whitespace-only text between blocks, but keep a
    // pending list marker (Google Docs writes <li><p>text</p></li>).
    const carriedPrefix = isBlank() ? prefix : '';
    flush(false);
    runs = [];
    prefix = carriedPrefix;

    const heading = tag.match(/^H([1-6])$/);
    const wordListLevel = styleAttr.match(/mso-list:[^;']*level(\d+)/i);

    if (heading && Number(heading[1]) > MAX_HEADING_LEVEL) {
      // Heading levels the editor doesn't offer arrive as plain text
      hasHeadingMarkup = true;
      childContext.plain = true;
    } else if (heading) {
      hasHeadingMarkup = true;
      setPrefix(`${'#'.repeat(Number(heading[1]))} `);
      childContext.inHeading = true;
    } else if (/MsoTitle\b/.test(className)) {
      hasHeadingMarkup = true;
      setPrefix('# ');
      childContext.inHeading = true;
    } else if (/MsoSubtitle\b/.test(className)) {
      hasHeadingMarkup = true;
      setPrefix('## ');
      childContext.inHeading = true;
    } else if (tag === 'BLOCKQUOTE' || /Mso(Intense)?Quote\b/.test(className)) {
      setPrefix('> ');
    } else if (tag === 'UL' || tag === 'OL') {
      childContext.listDepth = context.listDepth + 1;
      childContext.ordered = tag === 'OL';
    } else if (tag === 'LI') {
      const indent = '  '.repeat(Math.max(context.listDepth - 1, 0));
      let marker = '- ';
      if (context.ordered) {
        const start = Number(element.parentElement?.getAttribute('start')) || 1;
        const index = Array.from(element.parentElement?.children ?? [])
          .filter((sibling) => sibling.tagName === 'LI')
          .indexOf(element);
        marker = `${start + index}. `;
      }
      setPrefix(indent + marker);
    } else if (wordListLevel) {
      // Word list paragraph: the real marker lives in the mso-list:Ignore span
      const markerText =
        Array.from(element.querySelectorAll<HTMLElement>('span'))
          .find((span) =>
            /mso-list:\s*Ignore/i.test(span.getAttribute('style') ?? '')
          )
          ?.textContent?.replace(/\s+/g, '') ?? '';
      const indent = '  '.repeat(Number(wordListLevel[1]) - 1);
      const ordered = /^[0-9a-zA-Z]{1,3}[.)]$/.test(markerText);
      setPrefix(indent + (ordered ? `${markerText} ` : '- '));
    }

    if (childContext.inHeading) {
      format = { ...format, bold: false };
      childContext.format = format;
    }

    element.childNodes.forEach((child) => walk(child, childContext));

    // An empty leaf block (e.g. <p>&nbsp;</p>) is an intentional blank line;
    // a wrapper around other blocks or a trailing <br> is not.
    const isLeaf = !element.querySelector(
      'p,div,li,h1,h2,h3,h4,h5,h6,blockquote,pre,tr,ul,ol,table'
    );
    flush(isLeaf && !afterBreak);
  };

  walk(root, rootContext);
  flush(false);

  return { lines, formatted, hasHeadingMarkup };
}

// Matches the toolbar (H1–H3); deeper headings are pasted as plain text
const MAX_HEADING_LEVEL = 3;
const HEADING_MAX_LENGTH = 200;
const HEADING_SIZE_RATIO = 1.08;

/**
 * Apps that copy without heading tags (Pages, TextEdit, Notes) only mark
 * headings with a larger font. Body text is the size most text uses; short
 * standalone lines noticeably larger than that become headings.
 *
 * Levels come from the sizes used by several headings (the document's heading
 * levels). A size used once, like a title or subtitle, takes the level of the
 * next repeated size at or below it, so Title/H1 become "#" and Subtitle/H2
 * "##", matching how Word's heading styles convert.
 */
const markHeadingsBySize = (lines: Line[]) => {
  const totals = new Map<number, number>();
  lines.forEach(({ size, length }) => {
    if (size && length) totals.set(size, (totals.get(size) ?? 0) + length);
  });
  const bodySize = [...totals.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  if (!bodySize) return false;

  const candidates = lines.filter(
    (line) =>
      !line.table &&
      !line.prefix &&
      line.size &&
      line.length > 0 &&
      line.length <= HEADING_MAX_LENGTH &&
      line.size >= bodySize * HEADING_SIZE_RATIO
  );

  const counts = new Map<number, number>();
  candidates.forEach(({ size }) =>
    counts.set(size!, (counts.get(size!) ?? 0) + 1)
  );
  const allSizes = [...counts.keys()].sort((a, b) => b - a);
  const repeated = allSizes.filter((size) => counts.get(size)! > 1);
  const levelSizes = repeated.length ? repeated : allSizes;
  const levelOf = (size: number) => {
    const index = levelSizes.findIndex((levelSize) => levelSize <= size);
    return (index === -1 ? levelSizes.length : index) + 1;
  };

  let marked = false;
  candidates.forEach((line) => {
    const level = levelOf(line.size!);
    if (level > MAX_HEADING_LEVEL) return;
    marked = true;
    line.prefix = `${'#'.repeat(level)} `;
    // A heading doesn't need its own bold markers
    const bold = line.text.match(/^\*\*(.+)\*\*$/);
    if (bold && !bold[1].includes('**')) line.text = bold[1];
  });
  return marked;
};
